import React, { useCallback, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    List,
    ListItem,
    ListItemText,
    ListSubheader,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SidebarNavItem from './SidebarNavItem';
import usePermissions from '../hooks/usePermissions';
import { getPmNavItems, getPmQuickActionItems } from '../config/pmNavConfig';
import { filterByPermission } from '../utils/permissions';

function QuickActionItem({ icon: Icon, label, to, onClick }) {
    return (
        <ListItem
            button
            component={to ? Link : 'div'}
            to={to}
            onClick={onClick}
            sx={{ py: 0.5, px: 2 }}
        >
            <ListItemText
                primary={(
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon fontSize="small" />
                        {label}
                    </span>
                )}
            />
        </ListItem>
    );
}

const QuickActions = ({ onOpenAssistant, collapsed = false }) => {
    const params = useParams();
    const location = useLocation();
    const { t } = useTranslation();
    const { has } = usePermissions();
    const orgId = params.orgId;
    const isActive = useCallback((path) => (
        location.pathname === path
        || (path !== '/' && location.pathname.startsWith(`${path}/`))
    ), [location.pathname]);

    const navItems = useMemo(() => {
        const items = filterByPermission(getPmNavItems(orgId, t), has);
        return items.map((item) => ({
            ...item,
            selected: item.to ? isActive(item.to) : false,
        }));
    }, [orgId, t, has, isActive]);

    const quickActionItems = useMemo(() => {
        const items = filterByPermission(getPmQuickActionItems(orgId, t), has);
        return items.map((item) => ({
            ...item,
            onClick: item.assistantIntent
                ? () => onOpenAssistant({ intent: item.assistantIntent })
                : (item.to ? undefined : () => onOpenAssistant()),
        }));
    }, [orgId, t, has, onOpenAssistant]);

    return (
        <List disablePadding>
            {navItems.map((item) => (
                <SidebarNavItem
                    key={item.label}
                    icon={item.icon}
                    label={item.label}
                    to={item.to}
                    selected={item.selected}
                    collapsed={collapsed}
                />
            ))}

            {collapsed ? (
                <SidebarNavItem
                    icon={SmartToyIcon}
                    label={t('app.chat.askAssistant')}
                    onClick={() => onOpenAssistant()}
                    collapsed
                />
            ) : (
                quickActionItems.length > 0 && (
                    <>
                        <ListSubheader
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: 'transparent',
                                color: 'text.primary',
                            }}
                        >
                            {t('app.chat.quickAssistantActions')}
                        </ListSubheader>
                        {quickActionItems.map((item) => (
                            <QuickActionItem
                                key={item.label}
                                icon={item.icon}
                                label={item.label}
                                to={item.to}
                                onClick={item.onClick}
                            />
                        ))}
                    </>
                )
            )}
        </List>
    );
};

export default QuickActions;
